import { opensTurn } from '@littlebird/ui-utils';

interface MessageLike {
  id: string;
  role: string;
  joinedTurn?: boolean;
}

export function excludeGroupAnswersFromLiveTurn<T extends MessageLike>(
  messages: T[],
  groupAnswerIds: ReadonlySet<string>
): T[] {
  if (groupAnswerIds.size === 0) return messages;
  return messages.filter((message) => !groupAnswerIds.has(message.id));
}

interface LiveStartItemLike {
  type: string;
  message?: {
    id: string;
  };
}

export function findLastTurnStartId(messages: MessageLike[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (opensTurn(messages[i])) return messages[i].id;
  }
  return null;
}

export function findNewTurnStartIdAfterAnchor(
  messages: MessageLike[],
  anchorMessageId: string | null
): string | null {
  const anchorIndex = anchorMessageId
    ? messages.findIndex((message) => message.id === anchorMessageId)
    : -1;

  for (let i = anchorIndex + 1; i < messages.length; i++) {
    if (opensTurn(messages[i])) return messages[i].id;
  }

  return null;
}

export function findNewTailTurnStartId(
  messages: MessageLike[],
  previousTurnStartId: string | null
): string | null {
  if (!previousTurnStartId) return null;
  const turnStartId = findLastTurnStartId(messages);
  if (!turnStartId || turnStartId === previousTurnStartId) return null;
  const previousIsStillThere = messages.some(
    (message) => message.id === previousTurnStartId
  );
  return previousIsStillThere ? turnStartId : null;
}

export function resolveLiveStartMessageIdForSendEvent(
  messages: MessageLike[],
  anchorMessageId: string | null,
  messageCountAtEvent: number
): string | null {
  const nextLiveStartMessageId = findNewTurnStartIdAfterAnchor(
    messages,
    anchorMessageId
  );

  if (nextLiveStartMessageId) return nextLiveStartMessageId;
  if (!anchorMessageId) return null;
  if (messages.length !== messageCountAtEvent) return null;

  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.role === 'user' && lastMessage.id === anchorMessageId) {
    return anchorMessageId;
  }

  return null;
}

export function resolveLiveStartIndex(
  chatItems: LiveStartItemLike[],
  liveStartMessageId: string | null
): number | null {
  if (!liveStartMessageId) return null;

  const liveUserIndex = chatItems.findIndex(
    (item) =>
      item.type === 'userMessage' && item.message?.id === liveStartMessageId
  );

  if (liveUserIndex === -1) return null;

  const previousItem = chatItems[liveUserIndex - 1];
  if (previousItem?.type === 'dateSeparator') return liveUserIndex - 1;

  return liveUserIndex;
}

export function shouldDeactivateLiveRegion(
  isBusy: boolean,
  liveOffScreen: boolean
): boolean {
  return !isBusy && liveOffScreen;
}
